import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../elements/icon';
import { getBrowserLocale, normalizeLanguageCode } from '../../../utils/language';

function getVisibleCards(width) {
  if (width >= 1200) return 4;
  if (width >= 768) return 2;
  return 1;
}

function formatRelativeDate(dateValue, locale) {
  if (!dateValue) return '';

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = date.getTime() - Date.now();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const absDays = Math.abs(diffDays);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (absDays < 7) {
    return rtf.format(diffDays, 'day');
  }

  if (absDays < 30) {
    return rtf.format(Math.round(diffDays / 7), 'week');
  }

  if (absDays < 365) {
    return rtf.format(Math.round(diffDays / 30), 'month');
  }

  return rtf.format(Math.round(diffDays / 365), 'year');
}

function ReviewCard({ review }) {
  const { t, i18n } = useTranslation();
  const locale = getBrowserLocale(i18n.language);
  const reviewerName = review?.reviewer?.first_name || review?.reviewer?.name || t('Omega3_Reviews_VerifiedCustomer');
  const reviewBody = review?.review || review?.comments || '';
  const rating = Number(review?.rating || 0);
  const reviewDate = formatRelativeDate(
    review?.date_created || review?.created_at || review?.createdAt || review?.date,
    locale,
  );

  return (
    <article className="t-flex t-h-full t-flex-col t-justify-between t-bg-white t-p-5 lg:t-p-6 t-min-h-[260px] t-rounded-lg">
      <div>
        <div className="t-flex t-items-center t-gap-2 t-flex-wrap">
          <h3 className="t-m-0 t-text-[15px] t-font-semibold t-text-navy">{reviewerName}</h3>
          <div
            className="t-flex t-items-center t-gap-1 t-text-[#F6E147] t-text-sm"
            aria-label={`${rating} stars`}
          >
            {Array.from({ length: 5 }).map((_, index) => (
              <span key={`star-${review?.product_review_id || reviewerName}-${index}`}>
                {index < rating ? '★' : '☆'}
              </span>
            ))}
          </div>
        </div>

        {review?.reviewer?.verified_buyer === 'yes' && (
          <div className="t-mt-2 t-flex t-items-center t-gap-2 t-text-[12px] t-font-medium t-text-navy/80">
            <span className="t-inline-flex t-h-4 t-w-4 t-items-center t-justify-center t-rounded-full t-bg-navy t-text-[10px] t-text-white">
              ✓
            </span>
            <span>{t('Omega3_Reviews_VerifiedCustomer')}</span>
          </div>
        )}

        <p className="t-m-0 t-mt-3 t-text-[15px] t-leading-7 t-text-navy/90 line-clamp-5">{reviewBody}</p>
      </div>

      <p className="t-m-0 t-mt-5 t-text-right t-text-[12px] t-text-navy/60">{reviewDate}</p>
    </article>
  );
}

const Omega3Reviews = () => {
  const { t } = useTranslation();

  return (
    <section id="omega3-reviews" className="t-bg-gray-100 t-py-16 md:t-py-20">
      <div className="container !t-max-w-[1200px] t-flex t-flex-col t-gap-4">
        <h2
          className="!t-m-0 t-text-[34px] lg:t-text-[42px] t-leading-[1.08] t-text-navy t-text-center"
          style={{ fontFamily: 'DM Serif Display, serif' }}
        >
          {t('Omega3_Reviews_Title')}
        </h2>
        <p className="t-text-center !t-m-0 t-font-sans t-text-[15px] t-text-navy/70">
          {t('Omega3_Reviews_Subtitle')}
        </p>
        <ReviewsCarousel />
      </div>
    </section>
  );
};

const ReviewsCarousel = ({ sku = 'OMEGA3' }) => {
  const { t, i18n } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(() =>
    typeof window === 'undefined' ? 4 : getVisibleCards(window.innerWidth),
  );

  const selectedLang = normalizeLanguageCode(i18n.language);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await fetch(`https://api.reviews.io/product/review?store=weriise.co&sku=${sku}&lang=${selectedLang}`);
        const data = await res.json();
        console.log('DATA => ', data);
        setReviews(data?.reviews?.data || []);
        setStats(data?.stats || null);
      } catch (err) {
        console.error(err);
      }
    };

    fetchReviews();
  }, [selectedLang, sku]);

  useEffect(() => {
    const onResize = () => {
      setVisibleCards(getVisibleCards(window.innerWidth));
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const maxIndex = Math.max(reviews.length - visibleCards, 0);

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  useEffect(() => {
    if (reviews.length <= visibleCards) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 5000);

    return () => window.clearInterval(interval);
  }, [maxIndex, reviews.length, visibleCards]);

  const averageLabel = useMemo(() => {
    const average = Number(stats?.average || 0);
    if (!average) return null;
    return average.toFixed(1);
  }, [stats]);

  const countLabel = useMemo(() => {
    const count = Number(stats?.count || reviews.length || 0);
    return Number.isFinite(count) ? count : 0;
  }, [reviews.length, stats]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  if (!reviews.length) {
    return null;
  }

  return (
    <div className="t-relative t-mt-2">
      <div className="t-flex t-flex-col t-items-center t-gap-2 t-text-center">
        <div className="t-flex t-flex-wrap t-items-center t-justify-center t-gap-3 t-text-[15px] t-text-navy">
          <span className="t-font-semibold">{t('Omega3_Reviews_Excellent')}</span>
          <div className="t-flex t-items-center t-gap-1 t-text-[#F6E147]" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, index) => (
              <span key={`summary-star-${index}`}>★</span>
            ))}
          </div>
          <span>
            {t('Omega3_Reviews_BasedOnReviews', { average: averageLabel, count: countLabel })}
          </span>
        </div>
      </div>

      <div className="t-mt-8 t-flex t-items-center t-gap-2 md:t-gap-3 lg:t-gap-5">
        {reviews.length > visibleCards && (
          <button
            type="button"
            aria-label={t('Omega3_Reviews_Previous')}
            className="t-inline-flex t-h-8 t-w-8 t-shrink-0 t-items-center t-justify-center t-rounded-full t-border t-border-navy/15 t-bg-white t-text-navy/70 hover:t-text-navy"
            onClick={handlePrev}
          >
            <Icon name="chevronLeft" size="20" />
          </button>
        )}

        <div className="t-min-w-0 t-flex-1 t-overflow-hidden">
          <div
            className="t-flex t-transition-transform t-duration-500 t-ease-out"
            style={{ transform: `translateX(-${(100 / visibleCards) * currentIndex}%)` }}
          >
            {reviews.map((review) => (
              <div
                key={review.product_review_id}
                className="t-box-border t-shrink-0 t-px-1.5 md:t-px-2"
                style={{ flex: `0 0 ${100 / visibleCards}%` }}
              >
                <ReviewCard review={review} />
              </div>
            ))}
          </div>
        </div>

        {reviews.length > visibleCards && (
          <button
            type="button"
            aria-label={t('Omega3_Reviews_Next')}
            className="t-inline-flex t-h-8 t-w-8 t-shrink-0 t-items-center t-justify-center t-rounded-full t-border t-border-navy/15 t-bg-white t-text-navy/70 hover:t-text-navy"
            onClick={handleNext}
          >
            <Icon name="chevronRight" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Omega3Reviews;
